/**
 * Persoana de contact a echipei Ecotic, afisata scolilor «ocupate» (V2, lansarea 21.09).
 *
 * 🔴 NICIO valoare literala in cod: numele si telefonul unei persoane reale nu intra in repo. Vin din
 * variabilele de build `VITE_CONTACT_ECOTIC_NUME` / `VITE_CONTACT_ECOTIC_TELEFON`, puse in
 * `deploy-frontend.yml` si `deploy-frontend-test.yml` ca `${{ vars.… }}`. Cand owner-ul completeaza
 * `vars` in GitHub, urmatorul build le are — fara alta schimbare de cod.
 *
 * Cat timp variabilele sunt goale (azi: valorile vin de la client), `CONTACT_ECOTIC` e `null` si
 * randul de contact NU se afiseaza deloc — nu apare «Echipa Ecotic: , ».
 *
 * Numele variabilelor apar o SINGURA data, textual, mai jos: Vite le inlocuieste la build prin
 * potrivire pe text, deci un acces dinamic (`import.meta.env[nume]`) ar ramane `undefined`.
 */
export interface ContactEcotic {
    /** Poate fi gol daca s-a completat doar telefonul. */
    nume: string;
    /** Poate fi gol daca s-a completat doar numele. */
    telefon: string;
}

/** `null` cand nu e completat NIMIC (dupa trim) — semnalul „nu se randeaza randul". */
export function contactEcotic(
    nume: string | undefined | null,
    telefon: string | undefined | null,
): ContactEcotic | null {
    const n = String(nume ?? '').trim();
    const t = String(telefon ?? '').trim();
    if (!n && !t) return null;
    return { nume: n, telefon: t };
}

/** Prefixul international al Romaniei — `href`-ul de telefon de pe o pagina publica il cere. */
const PREFIX_ROMANIA = '+40';

/**
 * Valoarea pentru `href="tel:…"`: doar cifre si un `+` de la inceput. Un telefon scris «0722 123 456»
 * sau «(0722) 123-456» trebuie sa deschida formularea numarului, nu sa fie trunchiat la primul spatiu.
 * `''` cand nu ramane nicio cifra — apelantul nu face atunci link.
 *
 * 🔴 **Un numar national primeste prefixul de tara.** Pe o pagina publica, `href`-ul trebuie sa fie in
 * format international (RFC 2806: «international numbers MUST begin with the + character»; web.dev
 * «Click to Call»: «always supply the phone number using the international dialing format»). Cu
 * «0752 301 002» dat direct, iesea `tel:0752301002` — un numar ambiguu, care nu se formeaza corect
 * de pe un telefon cu alta regiune implicita sau in roaming. Textul afisat poate ramane in format
 * local; `href`-ul, nu.
 */
export function hrefTelefon(telefon: string): string {
    const t = telefon.trim();
    const cifre = t.replace(/\D/g, '');
    if (!cifre) return '';
    if (t.startsWith('+')) return `tel:+${cifre}`;
    // Un singur `0` de la inceput: numar national romanesc («0752 301 002» -> «+40752301002»).
    if (cifre.startsWith('0')) return `tel:${PREFIX_ROMANIA}${cifre.slice(1)}`;
    return `tel:${cifre}`;
}

export const CONTACT_ECOTIC = contactEcotic(
    import.meta.env.VITE_CONTACT_ECOTIC_NUME,
    import.meta.env.VITE_CONTACT_ECOTIC_TELEFON,
);
